import {
  CoreInspection,
  MaintenanceModel,
  MaintenancePaymentModel,
  MaintenanceRecurrenceModel,
} from 'api/models';
import MaintenanceTasks from 'containercomponents/MaintenanceTaskListContainerComponent/MaintenanceTasks/MaintenanceTasks';
import ButtonBase from 'corecomponents/ButtonBase/ButtonBase';
import { RequestType } from 'models/enums';
import { getImage } from 'utils/formatMaintenance';

interface MaintenanceTaskComponentProps {
  showMaintenanceModal: (
    task: MaintenanceModel | MaintenanceRecurrenceModel,
  ) => (appointmentId?: string) => void;
  tasks: MaintenanceModel[];
  isConsentPendingList?: boolean;
  isRenter: boolean;
}

const MaintenanceTaskComponent = ({
  showMaintenanceModal,
  tasks = [],
  isConsentPendingList = false,
  isRenter,
}: MaintenanceTaskComponentProps) => {
  return (
    <>
      {tasks.map((task) => {
        const image = getImage(task.requestType);

        // Both stay `undefined` on the paths that assign neither — a `Group`
        // task with no published invoices never sets the actual cost — and both
        // children below already test for that (`isNil`), so the type says so
        // rather than the initialisation changing.
        let displayEstimatedResponsibleCost:
          | ''
          | 'Pricing TBD'
          | MaintenancePaymentModel['estimatedAdjustmentApplied']
          | undefined;
        let displayResponsibleCost:
          | ''
          | 'Free'
          | MaintenancePaymentModel['actualAdjustmentApplied']
          | undefined;

        if ('involvedRole' in task && task.involvedRole === 'Resident') {
          // `payments` is optional on the model but this read was already
          // unconditional; the assertion keeps that throw.
          const renterResponsibility = task.payments!.find(
            (payment) => payment.paidBy === task.involvedRole,
          );

          // `percentage` is optional, and `undefined > 0` was already `false` —
          // the explicit check says the same thing and narrows
          // `renterResponsibility` for the branch.
          if (
            renterResponsibility?.percentage !== undefined &&
            renterResponsibility.percentage > 0
          ) {
            displayEstimatedResponsibleCost =
              renterResponsibility?.estimatedAdjustmentApplied || 'Pricing TBD';
            displayResponsibleCost =
              renterResponsibility.actualAdjustmentApplied;
          } else if (task.actualCost?.totalCost) {
            displayResponsibleCost = 'Free';
          } else {
            displayEstimatedResponsibleCost = '';
            displayResponsibleCost = '';
          }
        }

        if ('involvedRole' in task && task.involvedRole === 'Homeowner') {
          const homeOwnerResponsibility = task.payments!.find(
            (payment) => payment.paidBy === task.involvedRole,
          );
          if (task.requestType === RequestType.Group) {
            displayEstimatedResponsibleCost =
              homeOwnerResponsibility?.estimatedNetTotal || 'Pricing TBD';
            if (task.requestTypeDetails?.hasPublishedInvoices) {
              displayResponsibleCost = homeOwnerResponsibility?.actualNetTotal;
            }
          } else {
            if (
              homeOwnerResponsibility?.percentage !== undefined &&
              homeOwnerResponsibility.percentage > 0
            ) {
              displayEstimatedResponsibleCost =
                homeOwnerResponsibility?.estimatedAdjustmentApplied ||
                'Pricing TBD';
              displayResponsibleCost =
                homeOwnerResponsibility.actualAdjustmentApplied;
            } else if (task.actualCost?.totalCost) {
              displayResponsibleCost = 'Free';
            } else {
              displayEstimatedResponsibleCost = '';
              displayResponsibleCost = '';
            }
          }
        }

        return (
          <ButtonBase
            onClick={() => showMaintenanceModal(task)()}
            className="mb-xs w-full last-of-type:mb-0"
            key={task.id}
          >
            <MaintenanceTasks
              // `inspection` is not on `MaintenanceModel`, so this read has
              // always fallen through to `createdOn`. Kept, and cast rather
              // than deleted, because the generated models lag the API and
              // removing it would change behaviour if the field comes back —
              // see the followups doc.
              maintenanceDate={
                (task as MaintenanceModel & { inspection?: CoreInspection })
                  .inspection?.completedOn || task.createdOn
              }
              maintenanceTask={task.summary}
              maintenanceTaskImage={image}
              estimatedCost={displayEstimatedResponsibleCost}
              actualCost={displayResponsibleCost}
              priceRange={'priceRange' in task ? task.priceRange : undefined}
              numberOfOptions={
                'numberOfOptions' in task ? task.numberOfOptions : undefined
              }
              requestCategory={
                'requestCategory' in task ? task.requestCategory : undefined
              }
              requestType={'requestType' in task ? task.requestType : undefined}
              maintenanceTaskStatus={
                'status' in task ? task?.status : undefined
              }
              isConsentPendingList={isConsentPendingList}
              isRenter={isRenter}
              task={task}
              onOutcomeClick={showMaintenanceModal(task)}
            />
          </ButtonBase>
        );
      })}
    </>
  );
};

export default MaintenanceTaskComponent;
